import {
    Check,
    ChevronDown,
    type LucideIcon,
    PanelRight,
    PictureInPicture2,
    SlidersHorizontal,
    Square,
} from 'lucide-react';

import {
    CHAT_MODE_LABELS,
    headerLayoutDestination,
    headerLayoutIcon,
} from '@apps/web/components/phoebe_chat/chat_mode_icons';
import { Button } from '@apps/web/components/ui/button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuRadioGroup,
    DropdownMenuRadioItem,
    DropdownMenuTrigger,
} from '@apps/web/components/ui/dropdown_menu';
import { type ChatPanelMode } from '@apps/web/lib/chat_ui_persistence';
import { cn } from '@apps/web/lib/utils';

type ChatModeOption = {
    readonly mode: ChatPanelMode;
    readonly label: string;
    readonly icon: LucideIcon;
};

// The v2 chat view's dropdown. The v3 panel uses `ChatModeRail` below with the
// shared glyphs from `chat_mode_icons`; this list is left alone so replacing
// the v3 control cannot regress the surface it is replacing.
const CHAT_MODE_OPTIONS: ReadonlyArray<ChatModeOption> = [
    { mode: 'sidebar', label: 'Sidebar', icon: PanelRight },
    { mode: 'floating', label: 'Floating', icon: PictureInPicture2 },
    { mode: 'fullscreen', label: 'Full screen', icon: Square },
];

// One exit from the current size. The panel already states which size it is,
// so a three-up rail with a pressed segment is redundant.
export function ChatModeRail({
    chatMode,
    onChangeChatMode,
    className,
}: {
    readonly chatMode: ChatPanelMode;
    readonly onChangeChatMode: (next: ChatPanelMode) => void;
    readonly className?: string;
}) {
    const nextMode = headerLayoutDestination(chatMode);
    const Icon = headerLayoutIcon(chatMode);
    const label = CHAT_MODE_LABELS[nextMode];
    return (
        <Button
            type="button"
            size="icon"
            variant="ghost"
            aria-label={label}
            title={label}
            data-chat-mode-rail-item={nextMode}
            onClick={() => {
                onChangeChatMode(nextMode);
            }}
            className={cn('size-7', className)}
        >
            <Icon className="size-4" />
        </Button>
    );
}

export type NewChatRunType = 'general' | 'orchestrator';

const NEW_CHAT_RUN_TYPE_LABELS: Readonly<Record<NewChatRunType, string>> = {
    general: 'General',
    orchestrator: 'Orchestrator',
};

export function NewChatRunTypePicker({
    runType,
    runTypeOptions,
    disabled,
    className,
    onRunTypeChange,
}: {
    readonly runType: NewChatRunType;
    readonly runTypeOptions: ReadonlyArray<NewChatRunType>;
    readonly disabled: boolean;
    readonly className?: string;
    readonly onRunTypeChange: (runType: NewChatRunType) => void;
}) {
    return (
        <DropdownMenu>
            <DropdownMenuTrigger asChild={true}>
                <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    disabled={disabled}
                    data-new-chat-run-type-trigger="true"
                    className={cn(
                        'bg-muted/70 hover:bg-muted h-7 gap-1 rounded-full px-2 text-xs font-medium',
                        className,
                    )}
                >
                    <SlidersHorizontal className="text-muted-foreground size-3 shrink-0" />
                    <span data-new-chat-run-type-label="true">
                        {NEW_CHAT_RUN_TYPE_LABELS[runType]}
                    </span>
                    <ChevronDown className="text-muted-foreground size-3 shrink-0" />
                </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-52 p-2">
                <div className="text-muted-foreground px-2 py-2 text-sm font-medium">
                    Select a run type
                </div>
                <DropdownMenuRadioGroup
                    value={runType}
                    onValueChange={(value) => {
                        const selected = runTypeOptions.find(
                            (option) => option === value,
                        );
                        if (selected !== undefined) {
                            onRunTypeChange(selected);
                        }
                    }}
                >
                    {runTypeOptions.map((option) => (
                        <DropdownMenuRadioItem key={option} value={option}>
                            {NEW_CHAT_RUN_TYPE_LABELS[option]}
                        </DropdownMenuRadioItem>
                    ))}
                </DropdownMenuRadioGroup>
            </DropdownMenuContent>
        </DropdownMenu>
    );
}

export function ChatModeSelector({
    chatMode,
    onChangeChatMode,
}: {
    readonly chatMode: ChatPanelMode;
    readonly onChangeChatMode: (next: ChatPanelMode) => void;
}) {
    const TriggerIcon =
        CHAT_MODE_OPTIONS.find((option) => option.mode === chatMode)?.icon ??
        PanelRight;
    // No surrounding Tooltip: Tooltip + menu-trigger composition keeps the
    // tooltip painted over the open menu. The label covers screen readers.
    return (
        <DropdownMenu>
            <DropdownMenuTrigger asChild={true}>
                <Button
                    type="button"
                    size="icon"
                    variant="ghost"
                    aria-label="Switch chat mode"
                    className="size-7"
                >
                    <TriggerIcon className="h-4 w-4" />
                </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-44">
                {CHAT_MODE_OPTIONS.map(({ mode, label, icon: Icon }) => (
                    <DropdownMenuItem
                        key={mode}
                        onClick={() => {
                            onChangeChatMode(mode);
                        }}
                    >
                        <Icon className="mr-2 h-4 w-4" />
                        <span className="flex-1">{label}</span>
                        {chatMode === mode ? (
                            <Check className="ml-2 h-4 w-4" />
                        ) : null}
                    </DropdownMenuItem>
                ))}
            </DropdownMenuContent>
        </DropdownMenu>
    );
}
